import { useState } from "react";
import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Input } from "@/components/ui/input";
import { Panel, PanelLabel } from "@/components/PageHeader";
import { COMP_COLORS, COMP_LABELS, CURRENT_LABEL, type CompGroup, type DashboardResponse, type Period } from "@/lib/dashboard";
import { wholeMoney, money } from "@/lib/format";

const ORDER: CompGroup[] = ["essentials", "comfort", "debt", "savings_investment", "uncategorized"];

export function Composition({ d, period }: { d: DashboardResponse; period: Period }) {
  const g = d.composition.groups;
  const total = ORDER.reduce((s, k) => s + g[k], 0);
  const pct = (v: number) => (total > 0 ? Math.round((v / total) * 100) : 0);
  const max = Math.max(...d.composition.categories.map((c) => c.amount), 1);
  return (
    <Panel testId="composition">
      <PanelLabel>Composición real de tus gastos</PanelLabel>
      <p className="mt-1 text-sm text-slate-500">En qué se fue tu dinero {CURRENT_LABEL[period]}: {money(total)}</p>
      <div className="mt-4 flex h-3 overflow-hidden rounded-full bg-[#F4F5F7]" data-testid="composition-bar">
        {ORDER.filter((k) => g[k] > 0).map((k) => <div key={k} style={{ width: `${pct(g[k])}%`, background: COMP_COLORS[k] }} className="transition-[width] duration-500" />)}
      </div>
      <ul className="mt-4 grid grid-cols-2 gap-3">
        {ORDER.filter((k) => k !== "uncategorized" || g[k] > 0).map((k) => (
          <li key={k} className="text-sm" data-testid={`composition-group-${k}`}>
            <span className="flex items-center gap-2 text-slate-500"><i className="h-2.5 w-2.5 rounded-full" style={{ background: COMP_COLORS[k] }} />{COMP_LABELS[k]}</span>
            <span className="tabular font-semibold text-[#1B2A4A]">{money(g[k])} <span className="font-normal text-slate-400">· {pct(g[k])}%</span></span>
          </li>
        ))}
      </ul>
      <p className="mt-5 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">Por categoría</p>
      {d.composition.categories.length === 0 && <p className="mt-2 text-sm text-slate-500">Aún no hay gastos en este periodo.</p>}
      <ul className="mt-3 space-y-3">
        {d.composition.categories.slice(0, 8).map((c) => (
          <li key={`${c.name}-${c.group}`} data-testid={`composition-cat-${c.name}`}>
            <div className="flex justify-between gap-2 text-sm"><span className="min-w-0 text-slate-600">{c.name}{c.group !== "savings_investment" && <span className="text-slate-400"> · {COMP_LABELS[c.group]}</span>}</span><span className="tabular font-semibold text-[#1B2A4A]">{money(c.amount)}</span></div>
            <div className="mt-1 h-1.5 rounded-full bg-[#F4F5F7]"><div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${(c.amount / max) * 100}%`, background: COMP_COLORS[c.group] }} /></div>
          </li>
        ))}
      </ul>
    </Panel>
  );
}

export function Projection({ d }: { d: DashboardResponse }) {
  const [rate, setRate] = useState("");
  const r = Math.max(Number(rate.replace(",", ".")) || 0, 0) / 100;
  const s = d.projection.avg_monthly_savings;
  const investable = (d.liquid_total ?? 0) + d.invested_total;
  const others = d.net_worth - investable;
  const rm = (1 + r) ** (1 / 12) - 1;
  const base = (y: number) => d.net_worth + s * 12 * y;
  const withReturn = (y: number) => others + investable * (1 + r) ** y + (rm > 0 ? s * (((1 + rm) ** (12 * y) - 1) / rm) : s * 12 * y);
  const data = Array.from({ length: 16 }, (_, y) => ({ year: y === 0 ? "Hoy" : `${y}a`, base: Math.round(base(y)), ret: Math.round(withReturn(y)) }));
  return (
    <Panel testId="projection">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <PanelLabel>Si sigues al mismo ritmo</PanelLabel>
          <p className="mt-1 text-sm text-slate-500" data-testid="projection-basis">
            Ahorras en promedio <b className={s >= 0 ? "text-[#1B6E4F]" : "text-[#B23A1E]"}>{money(s)}</b> al mes (ingresos − gastos de los últimos {d.projection.months_considered} meses).
          </p>
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600">
          Rendimiento anual de tu dinero
          <span className="relative"><Input value={rate} inputMode="decimal" placeholder="0" onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRate(e.target.value.replace(/[^\d.,]/g, "").slice(0, 5))} className="h-10 w-20 pr-6 text-right" data-testid="projection-rate-input" /><span className="absolute right-2 top-2.5 text-slate-400">%</span></span>
        </label>
      </div>
      <div className="mt-5 grid gap-3 sm:grid-cols-3">
        {[5, 10, 15].map((y) => (
          <div key={y} className="rounded-xl bg-[#F4F5F7] p-4" data-testid={`projection-${y}`}>
            <p className="text-xs font-bold uppercase tracking-[0.14em] text-slate-500">En {y} años</p>
            <p className={`tabular mt-1 font-display text-2xl font-extrabold ${base(y) < 0 ? "text-[#B23A1E]" : "text-[#1B2A4A]"}`} data-testid={`projection-${y}-base`}>{money(base(y))}</p>
            <p className="tabular text-xs text-slate-500">Sin rendimientos</p>
            {r > 0 && <p className="tabular mt-2 text-sm font-semibold text-[#2C4F91]" data-testid={`projection-${y}-return`}>{money(withReturn(y))} <span className="font-normal text-slate-500">con {rate}% anual</span></p>}
          </div>
        ))}
      </div>
      <div className="mt-5 h-60">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
            <CartesianGrid stroke="#ECE3D3" vertical={false} />
            <XAxis dataKey="year" tick={{ fontSize: 11, fill: "#5A6170" }} axisLine={false} tickLine={false} />
            <YAxis tickFormatter={wholeMoney} tick={{ fontSize: 11, fill: "#5A6170" }} axisLine={false} tickLine={false} width={96} />
            <Tooltip formatter={(v) => money(Number(v))} contentStyle={{ borderRadius: 10, border: "1px solid #DDE1E8" }} />
            <Legend iconType="circle" wrapperStyle={{ fontSize: 12 }} />
            <Line dataKey="base" name="Sin rendimientos" stroke="#1A1F2B" strokeWidth={2.5} dot={false} />
            {r > 0 && <Line dataKey="ret" name={`Con ${rate}% anual`} stroke="#2C4F91" strokeWidth={2.5} dot={false} />}
          </LineChart>
        </ResponsiveContainer>
      </div>
      <p className="mt-3 text-xs text-slate-400">Es una estimación sencilla, sin inflación ni impuestos. El rendimiento se aplica a lo que tienes hoy en tus cuentas de débito, ahorro, efectivo e inversión ({money(investable)}) y a lo que ahorras cada mes. Tus deudas no cambian.</p>
    </Panel>
  );
}
